// Date and time formatting utilities for HR Portal (Singapore GMT+8 timezone)
// ALL date/time operations in the frontend MUST go through this module.
// See .cursor/rules/timezone-sgt-standardization.mdc for the full policy.

const SGT_TIMEZONE = 'Asia/Singapore';
const SGT_LOCALE = 'en-SG';

/**
 * Canonical react-datepicker `dateFormat` tokens — space-separated `dd mmm yyyy`.
 * Matches `formatDate()` display style. Never use hyphens (`dd-MMM-yyyy`) or
 * numeric slashes (`dd/MM/yyyy`) for human-readable date fields.
 */
export const DATE_PICKER_FORMAT = 'dd MMM yyyy';

/** Canonical react-datepicker format for date+time fields. */
export const DATE_PICKER_DATETIME_FORMAT = 'dd MMM yyyy h:mm aa';

/**
 * Get current date and time in Singapore timezone
 * @returns {Date} Current date/time in Singapore timezone
 */
export const getCurrentSingaporeTime = () => {
  return new Date(new Date().toLocaleString('en-US', { timeZone: SGT_TIMEZONE }));
};

/**
 * Convert any date to Singapore timezone
 * @param {string|Date} date - Date to convert
 * @returns {Date} Date converted to Singapore timezone
 */
export const toSingaporeTime = (date) => {
  if (!date) return null;
  const dateObj = new Date(date);
  return new Date(dateObj.toLocaleString('en-US', { timeZone: SGT_TIMEZONE }));
};

/**
 * Convert an SGT-frame Date (as returned by toSingaporeTime/getCurrentSingaporeTime,
 * i.e. a Date whose LOCAL getters read Singapore wall-clock values) into an
 * absolute ISO-8601 string with an explicit +08:00 offset.
 *
 * Use this when SENDING a wall-clock time the user picked (e.g. an OT end time)
 * to the backend. Never use `.toISOString()` on a Date built with `setHours()`
 * for this purpose — that interprets the wall-clock values in the DEVICE
 * timezone, so a phone set to another country shifts the submitted timestamp.
 *
 * @param {Date} sgtFrameDate - Date in the SGT frame
 * @returns {string|null} e.g. "2026-06-11T21:00:00+08:00", or null if invalid
 */
export const sgtWallTimeToISOString = (sgtFrameDate) => {
  if (!sgtFrameDate || !(sgtFrameDate instanceof Date) || isNaN(sgtFrameDate.getTime())) {
    return null;
  }
  const y = sgtFrameDate.getFullYear();
  const m = String(sgtFrameDate.getMonth() + 1).padStart(2, '0');
  const d = String(sgtFrameDate.getDate()).padStart(2, '0');
  const hh = String(sgtFrameDate.getHours()).padStart(2, '0');
  const mm = String(sgtFrameDate.getMinutes()).padStart(2, '0');
  const ss = String(sgtFrameDate.getSeconds()).padStart(2, '0');
  return `${y}-${m}-${d}T${hh}:${mm}:${ss}+08:00`;
};

/**
 * ISO timestamp → `YYYY-MM-DDTHH:mm` in Asia/Singapore wall clock.
 * Use for split date + time form values (clock-event edits, etc.).
 *
 * @param {string|Date|null|undefined} isoString
 * @returns {string}
 */
export const toSGTDateTimeLocal = (isoString) => {
  if (!isoString) return '';
  const dateObj = isoString instanceof Date ? isoString : new Date(isoString);
  if (Number.isNaN(dateObj.getTime())) return '';
  return dateObj
    .toLocaleString('sv-SE', { timeZone: SGT_TIMEZONE })
    .slice(0, 16)
    .replace(' ', 'T');
};

/**
 * Split a `YYYY-MM-DDTHH:mm` form value into date and HH:mm parts.
 *
 * @param {string|null|undefined} value
 * @returns {{ date: string, time: string }}
 */
export const splitDateTimeLocal = (value) => {
  if (!value) return { date: '', time: '' };
  const [date = '', timePart = ''] = String(value).split('T');
  return { date, time: timePart.slice(0, 5) };
};

/**
 * Join a YYYY-MM-DD date and HH:mm time into a `YYYY-MM-DDTHH:mm` form value.
 *
 * @param {string} date
 * @param {string} time
 * @returns {string}
 */
export const joinDateTimeLocal = (date, time) => {
  if (!date) return '';
  const normalizedTime = String(time || '00:00').slice(0, 5);
  return `${date}T${normalizedTime}`;
};

/**
 * Convert a `YYYY-MM-DDTHH:mm` wall-clock form value to an explicit SGT ISO
 * timestamp for the API. Does not interpret the string in the browser timezone.
 *
 * @param {string|null|undefined} value
 * @returns {string|null} e.g. "2026-08-28T19:00:00+08:00"
 */
export const dateTimeLocalToSgtISO = (value) => {
  const { date, time } = splitDateTimeLocal(value);
  if (!date || !time) return null;
  const [y, m, d] = date.split('-').map(Number);
  const [hh, mm] = time.split(':').map(Number);
  if ([y, m, d, hh, mm].some((n) => Number.isNaN(n))) return null;
  return sgtWallTimeToISOString(new Date(y, m - 1, d, hh, mm, 0));
};

/**
 * Convert a Date object to YYYY-MM-DD string using local date parts.
 * Safe for date picker values where the Date object represents the user's selection
 * in browser local time (which is SGT for Singapore-based users).
 *
 * This is the canonical version — do NOT redefine in components.
 *
 * @param {Date} date - Date object from date picker or local construction
 * @returns {string} Date string in YYYY-MM-DD format, or '' if invalid
 */
export const toLocalISODate = (date) => {
  if (!date) return '';
  if (date instanceof Date && isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/**
 * Parse a YYYY-MM-DD string into a Date object without UTC midnight shift.
 * Using `new Date('2026-03-16')` parses as UTC midnight, which can shift the day
 * when displayed in SGT. This function constructs the Date in local time instead.
 *
 * This is the canonical version — do NOT redefine in components.
 * Also aliased as parseLocalISOToDate for backward compatibility.
 *
 * @param {string} iso - Date string in YYYY-MM-DD format
 * @returns {Date|null} Date object in local time, or null if invalid
 */
export const parseLocalDate = (iso) => {
  if (!iso) return null;
  const parts = iso.split('-').map(Number);
  if (parts.length !== 3 || parts.some(isNaN)) return null;
  const [y, m, d] = parts;
  return new Date(y, m - 1, d);
};

/**
 * Add calendar months to a YYYY-MM-DD date without UTC shift.
 * Clamps to the last day of the target month (31 Jan + 1 month → 28/29 Feb).
 *
 * @param {string|Date} date - Start date
 * @param {number|string} months - Whole months to add
 * @returns {string} YYYY-MM-DD, or '' if invalid
 */
export const addCalendarMonthsToISO = (date, months) => {
  const start = date instanceof Date ? date : parseLocalDate(date);
  const count = Number(months);
  if (!start || Number.isNaN(start.getTime()) || !Number.isFinite(count)) return '';
  const target = new Date(start.getFullYear(), start.getMonth() + count, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  return toLocalISODate(new Date(target.getFullYear(), target.getMonth(), Math.min(start.getDate(), lastDay)));
};

/**
 * Inclusive last employment day from notice date + contract months.
 * MOM counts the day notice is given as day one (17 Aug + 1 month → 16 Sep).
 * When the corresponding date is clamped to a shorter month, that last
 * calendar day is the last day (29–31 Jan 2025 → 28 Feb 2025).
 *
 * @param {string|Date} noticeDate
 * @param {number|string} months
 * @returns {string} YYYY-MM-DD, or '' if invalid
 */
export const suggestEmploymentEndISO = (noticeDate, months) => {
  const start = noticeDate instanceof Date ? noticeDate : parseLocalDate(noticeDate);
  const corresponding = addCalendarMonthsToISO(noticeDate, months);
  if (!start || !corresponding) return '';
  const correspondingDate = parseLocalDate(corresponding);
  if (!correspondingDate) return '';
  if (correspondingDate.getDate() < start.getDate()) return corresponding;
  return toLocalISODate(new Date(
    correspondingDate.getFullYear(),
    correspondingDate.getMonth(),
    correspondingDate.getDate() - 1,
  ));
};

/** @deprecated Use parseLocalDate instead */
export const parseLocalISOToDate = parseLocalDate;

/**
 * Format date to "20 Jul 2025" / "02 Jul 2025" (dd mmm yyyy, Singapore timezone).
 * Zero-padded day; spaces between parts — never hyphens.
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDate = (date) => {
  if (!date) return 'N/A';
  
  try {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return 'Invalid Date';
    
    return dateObj.toLocaleDateString(SGT_LOCALE, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: SGT_TIMEZONE
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid Date';
  }
};

/**
 * Format time to 12-hour format without seconds (9:30 AM) in Singapore timezone
 * @param {string|Date} time - Time to format
 * @returns {string} Formatted time string
 */
export const formatTime = (time) => {
  if (!time) return 'N/A';
  
  try {
    const timeObj = new Date(time);
    if (isNaN(timeObj.getTime())) return 'Invalid Time';
    
    return timeObj.toLocaleTimeString(SGT_LOCALE, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: SGT_TIMEZONE
    });
  } catch (error) {
    console.error('Error formatting time:', error);
    return 'Invalid Time';
  }
};

/**
 * Format date and time together (20 Jul 2025 at 9:30 AM)
 * @param {string|Date} datetime - DateTime to format
 * @returns {string} Formatted datetime string
 */
export const formatDateTime = (datetime) => {
  if (!datetime) return 'N/A';
  
  try {
    const dateObj = new Date(datetime);
    if (isNaN(dateObj.getTime())) return 'Invalid DateTime';
    
    const dateStr = formatDate(dateObj);
    const timeStr = formatTime(dateObj);
    
    return `${dateStr} at ${timeStr}`;
  } catch (error) {
    console.error('Error formatting datetime:', error);
    return 'Invalid DateTime';
  }
};

/**
 * Format date and time in compact format (20 Jul 2025, 9:30 AM)
 * @param {string|Date} datetime - DateTime to format
 * @returns {string} Formatted datetime string in compact format
 */
export const formatDateTimeCompact = (datetime) => {
  if (!datetime) return 'N/A';
  
  try {
    const dateObj = new Date(datetime);
    if (isNaN(dateObj.getTime())) return 'Invalid DateTime';
    
    const dateStr = formatDate(dateObj);
    const timeStr = formatTime(dateObj);
    
    return `${dateStr}, ${timeStr}`;
  } catch (error) {
    console.error('Error formatting datetime compact:', error);
    return 'Invalid DateTime';
  }
};

/**
 * Event start for attendance-event list/detail headers.
 * Example: "Wed, 05 Aug 2026 · 8:15am" (drops ":00" on the hour).
 *
 * Weekday helps hosts scan "which day's briefing" among near-duplicates.
 *
 * @param {string|Date} datetime - Event start datetime
 * @returns {string} Formatted event start string
 */
export const formatEventStartAt = (datetime) => {
  if (!datetime) return 'N/A';

  try {
    const dateObj = new Date(datetime);
    if (isNaN(dateObj.getTime())) return 'Invalid DateTime';

    const weekday = dateObj.toLocaleDateString(SGT_LOCALE, {
      weekday: 'short',
      timeZone: SGT_TIMEZONE,
    });
    const dateStr = formatDate(dateObj);

    const parts = new Intl.DateTimeFormat(SGT_LOCALE, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: SGT_TIMEZONE,
    }).formatToParts(dateObj);
    const hour = parts.find((p) => p.type === 'hour')?.value;
    const minute = parts.find((p) => p.type === 'minute')?.value || '00';
    const dayPeriod = (parts.find((p) => p.type === 'dayPeriod')?.value || '').toLowerCase();
    const timeStr = minute === '00'
      ? `${hour}${dayPeriod}`
      : `${hour}:${minute.padStart(2, '0')}${dayPeriod}`;

    return `${weekday}, ${dateStr} · ${timeStr}`;
  } catch (error) {
    console.error('Error formatting event start:', error);
    return 'Invalid DateTime';
  }
};

/**
 * Format date for form inputs (YYYY-MM-DD) in Singapore timezone
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date string for input fields
 */
export const formatDateForInput = (date) => {
  if (!date) return '';
  
  try {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return '';

    // Use Intl parts in Asia/Singapore to avoid UTC conversion and off-by-one
    const parts = new Intl.DateTimeFormat(SGT_LOCALE, {
      timeZone: SGT_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(dateObj);

    const partMap = Object.fromEntries(parts.map(p => [p.type, p.value]));
    const year = partMap.year;
    const month = partMap.month;
    const day = partMap.day;
    if (!year || !month || !day) return '';
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('Error formatting date for input:', error);
    return '';
  }
};

/**
 * Format an absolute day count into a scannable span.
 *
 * HR mental model: under ~90 days they book renewals in day granularity;
 * past that, day counts ("3656d") force mental maths — months/years scan.
 *
 * Thresholds:
 *   < 90 days  → days (exact)
 *   < 365 days → months (rounded)
 *   ≥ 365 days → years, plus remaining months when ≥ 1
 *
 * @param {number} days - Day count (sign ignored; pass absolute or signed)
 * @param {'compact'|'long'} [style='compact'] - Badge chips vs prose
 * @returns {string} e.g. "45d" / "8mo" / "10y" / "1y 6mo", or long-form equivalents
 */
export const formatDaySpan = (days, style = 'compact') => {
  const n = Math.abs(Math.trunc(Number(days)));
  if (!Number.isFinite(n)) return style === 'compact' ? '—' : 'N/A';

  const plural = (count, unit) => `${count} ${unit}${count !== 1 ? 's' : ''}`;

  if (n < 90) {
    return style === 'compact' ? `${n}d` : plural(n, 'day');
  }

  if (n < 365) {
    const months = Math.max(1, Math.round(n / 30));
    return style === 'compact' ? `${months}mo` : plural(months, 'month');
  }

  const years = Math.floor(n / 365);
  const remMonths = Math.floor((n % 365) / 30);
  if (style === 'compact') {
    return remMonths > 0 ? `${years}y ${remMonths}mo` : `${years}y`;
  }
  if (remMonths > 0) {
    return `${plural(years, 'year')} ${plural(remMonths, 'month')}`;
  }
  return plural(years, 'year');
};

/**
 * StatusBadge / chip label for expiry urgency from signed days-until-expiry.
 *
 * @param {number|null|undefined} daysUntilExpiry - Negative = past, 0 = today, positive = remaining
 * @param {'compact'|'long'} [style='compact']
 * @returns {string|null} e.g. "Expired 10y ago", "45d left", "Expires today"
 */
export const formatExpiryUrgencyLabel = (daysUntilExpiry, style = 'compact') => {
  if (daysUntilExpiry == null || daysUntilExpiry === '') return null;
  const days = Math.trunc(Number(daysUntilExpiry));
  if (!Number.isFinite(days)) return null;

  if (days === 0) return 'Expires today';
  if (days < 0) return `Expired ${formatDaySpan(Math.abs(days), style)} ago`;
  return style === 'long'
    ? `${formatDaySpan(days, 'long')} left`
    : `${formatDaySpan(days, 'compact')} left`;
};

/**
 * Get relative time (e.g., "2 hours ago", "3 days ago")
 * @param {string|Date} date - Date to compare
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (date) => {
  if (!date) return 'N/A';
  
  try {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return 'Invalid Date';
    
    const now = getCurrentSingaporeTime();
    const sgtDate = toSingaporeTime(dateObj);
    const diff = now - sgtDate;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);
    
    if (years > 0) return `${years} year${years > 1 ? 's' : ''} ago`;
    if (months > 0) return `${months} month${months > 1 ? 's' : ''} ago`;
    if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    return 'Just now';
  } catch (error) {
    console.error('Error formatting relative time:', error);
    return 'Invalid Date';
  }
};

/**
 * Format duration in hours and minutes (e.g., "8h 30m")
 * @param {number} totalMinutes - Total minutes to format
 * @returns {string} Formatted duration string
 */
export const formatDuration = (totalMinutes) => {
  if (!totalMinutes || totalMinutes < 0) return '0h 0m';
  
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  
  return `${hours}h ${minutes}m`;
};

/**
 * Calculate duration between two timestamps in minutes
 * @param {string|Date} startTime - Start timestamp
 * @param {string|Date} endTime - End timestamp
 * @returns {number} Duration in minutes
 */
export const calculateDurationMinutes = (startTime, endTime) => {
  if (!startTime || !endTime) return 0;
  
  try {
    const start = new Date(startTime);
    const end = new Date(endTime);
    
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
    
    const diffMs = end - start;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    
    return Math.max(0, diffMinutes);
  } catch (error) {
    console.error('Error calculating duration:', error);
    return 0;
  }
};

/**
 * Calculate duration between two timestamps in hours (decimal)
 * @param {string|Date} startTime - Start timestamp
 * @param {string|Date} endTime - End timestamp
 * @returns {number} Duration in hours (decimal)
 */
export const calculateDurationHours = (startTime, endTime) => {
  if (!startTime || !endTime) return 0;
  
  try {
    const start = new Date(startTime);
    const end = new Date(endTime);
    
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
    
    const diffMs = end - start;
    const diffHours = diffMs / (1000 * 60 * 60);
    
    return Math.max(0, diffHours);
  } catch (error) {
    console.error('Error calculating duration hours:', error);
    return 0;
  }
};

/**
 * Format duration from milliseconds to hours and minutes
 * @param {number} milliseconds - Duration in milliseconds
 * @returns {string} Formatted duration string
 */
export const formatDurationFromMs = (milliseconds) => {
  if (!milliseconds || milliseconds < 0) return '0h 0m';
  
  const totalMinutes = Math.floor(milliseconds / (1000 * 60));
  return formatDuration(totalMinutes);
};

/**
 * Format decimal hours to hours and minutes (e.g., 1.5 -> "1h 30m")
 * @param {number} decimalHours - Hours in decimal format
 * @returns {string} Formatted duration string
 */
export const formatDecimalHours = (decimalHours) => {
  if (!decimalHours || decimalHours < 0) return '0h';
  
  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);

  if (minutes === 60) {
    return `${hours + 1}h`;
  }

  if (minutes === 0) {
    return `${hours}h`;
  }
  
  return `${hours}h ${minutes}m`;
};

/**
 * Check if a date is today
 * @param {string|Date} date - Date to check
 * @returns {boolean} True if date is today
 */
export const isToday = (date) => {
  if (!date) return false;
  
  try {
    const dateStr = extractDateInSingaporeTimezone(date);
    const todayStr = getTodayInSingaporeTimezone();
    return dateStr === todayStr;
  } catch (error) {
    return false;
  }
};

/**
 * Check if a date is this week
 * @param {string|Date} date - Date to check
 * @returns {boolean} True if date is this week
 */
export const isThisWeek = (date) => {
  if (!date) return false;
  
  try {
    const targetStr = extractDateInSingaporeTimezone(date);
    const todayStr = getTodayInSingaporeTimezone();
    if (!targetStr || !todayStr) return false;

    const [ty, tm, td] = todayStr.split('-').map(Number);
    const todayLocal = new Date(ty, tm - 1, td);
    const dayOfWeek = todayLocal.getDay();
    const startOfWeek = new Date(ty, tm - 1, td - dayOfWeek);
    const endOfWeek = new Date(ty, tm - 1, td - dayOfWeek + 6);

    const [dy, dm, dd] = targetStr.split('-').map(Number);
    const targetLocal = new Date(dy, dm - 1, dd);

    return targetLocal >= startOfWeek && targetLocal <= endOfWeek;
  } catch (error) {
    return false;
  }
};

/**
 * Get day of week name
 * @param {string|Date} date - Date to get day name from
 * @returns {string} Day name (e.g., "Monday")
 */
export const getDayName = (date) => {
  if (!date) return 'N/A';
  
  try {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return 'Invalid Date';
    
    return dateObj.toLocaleDateString('en-US', { weekday: 'long', timeZone: SGT_TIMEZONE });
  } catch (error) {
    return 'Invalid Date';
  }
};

/**
 * Get month name
 * @param {string|Date} date - Date to get month name from
 * @returns {string} Month name (e.g., "July")
 */
export const getMonthName = (date) => {
  if (!date) return 'N/A';
  
  try {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return 'Invalid Date';
    
    return dateObj.toLocaleDateString('en-US', { month: 'long', timeZone: SGT_TIMEZONE });
  } catch (error) {
    return 'Invalid Date';
  }
};

/**
 * Get short month name
 * @param {string|Date} date - Date to get month name from
 * @returns {string} Short month name (e.g., "Jul")
 */
export const getShortMonthName = (date) => {
  if (!date) return 'N/A';
  
  try {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return 'Invalid Date';
    
    return dateObj.toLocaleDateString('en-US', { month: 'short', timeZone: SGT_TIMEZONE });
  } catch (error) {
    return 'Invalid Date';
  }
};

/**
 * Format date for display in lists/tables (compact format)
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date string (20 Jul 2025)
 */
export const formatDateCompact = (date) => {
  return formatDate(date); // Uses the standardized format
};

/**
 * Format date for month/year display (Jul 2025)
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted month/year string
 */
export const formatMonthYear = (date) => {
  if (!date) return 'N/A';
  
  try {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return 'Invalid Date';
    
    return dateObj.toLocaleDateString(SGT_LOCALE, {
      month: 'short',
      year: 'numeric',
      timeZone: SGT_TIMEZONE
    });
  } catch (error) {
    console.error('Error formatting month/year:', error);
    return 'Invalid Date';
  }
};

/**
 * Extract date string (YYYY-MM-DD) from timestamp in Singapore timezone
 * @param {string|Date} timestamp - Timestamp to extract date from
 * @returns {string} Date string in YYYY-MM-DD format (Singapore timezone)
 */
export const extractDateInSingaporeTimezone = (timestamp) => {
  if (!timestamp) return null;
  
  try {
    const dateObj = new Date(timestamp);
    if (isNaN(dateObj.getTime())) return null;
    
    // Use Intl.DateTimeFormat with Singapore timezone to extract date parts
    const parts = new Intl.DateTimeFormat(SGT_LOCALE, {
      timeZone: SGT_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(dateObj);
    
    const partMap = Object.fromEntries(parts.map(p => [p.type, p.value]));
    return `${partMap.year}-${partMap.month}-${partMap.day}`;
  } catch (error) {
    console.error('Error extracting date in Singapore timezone:', error);
    return null;
  }
};

/**
 * Get today's date in Singapore timezone (YYYY-MM-DD format)
 * @returns {string} Today's date in YYYY-MM-DD format
 */
export const getTodayInSingaporeTimezone = () => {
  return extractDateInSingaporeTimezone(new Date());
};

/**
 * Calendar year for "today" in Singapore (aligns with Django `timezone.localdate().year`
 * when `TIME_ZONE` is Asia/Singapore).
 *
 * Prefer this over `new Date().getFullYear()` for HR calendar-year labels.
 *
 * @returns {number}
 */
export const getCurrentYearInSingaporeTimezone = () => {
  const ymd = getTodayInSingaporeTimezone();
  if (!ymd || ymd.length < 4) return new Date().getFullYear();
  const y = parseInt(ymd.slice(0, 4), 10);
  return Number.isFinite(y) ? y : new Date().getFullYear();
};

/**
 * Current month as YYYY-MM in Singapore timezone (for month pickers / API params).
 *
 * @returns {string}
 */
export const getCurrentMonthKeySingapore = () => {
  const ymd = getTodayInSingaporeTimezone();
  if (!ymd || ymd.length < 7) {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
  }
  return ymd.slice(0, 7);
};

/**
 * Get calendar days from today to target date (Singapore timezone).
 * Uses date-only comparison to avoid timezone/DST edge cases.
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {number} Positive = future, 0 = today, negative = past
 */
export const getCalendarDaysUntil = (dateStr) => {
  if (!dateStr) return null;
  const targetDateStr = extractDateInSingaporeTimezone(dateStr);
  const todayStr = getTodayInSingaporeTimezone();
  if (!targetDateStr || !todayStr) return null;
  const [ty, tm, td] = targetDateStr.split('-').map(Number);
  const [oy, om, od] = todayStr.split('-').map(Number);
  const targetDateOnly = new Date(ty, tm - 1, td);
  const todayDateOnly = new Date(oy, om - 1, od);
  const diffMs = targetDateOnly - todayDateOnly;
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
};

/**
 * Add days to a date string (YYYY-MM-DD) and return new date string
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @param {number} days - Number of days to add (can be negative)
 * @returns {string} New date string in YYYY-MM-DD format
 */
export const addDaysToDateString = (dateStr, days) => {
  if (!dateStr) return null;
  try {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    date.setDate(date.getDate() + days);
    const newYear = date.getFullYear();
    const newMonth = String(date.getMonth() + 1).padStart(2, '0');
    const newDay = String(date.getDate()).padStart(2, '0');
    return `${newYear}-${newMonth}-${newDay}`;
  } catch (error) {
    console.error('Error adding days to date string:', error);
    return null;
  }
}; 

/**
 * Format date without year (e.g., "22 Dec", "02 Jul")
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date string without year
 */
export const formatDateShort = (date) => {
  if (!date) return 'N/A';
  
  try {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return 'Invalid Date';
    
    return dateObj.toLocaleDateString(SGT_LOCALE, {
      day: '2-digit',
      month: 'short',
      timeZone: SGT_TIMEZONE
    });
  } catch (error) {
    console.error('Error formatting date short:', error);
    return 'Invalid Date';
  }
};

/**
 * Get the year of a date in Singapore timezone
 * @param {string|Date} date - Date to get year from
 * @returns {number|null} Year number or null if invalid
 */
export const getYearInSingapore = (date) => {
  if (!date) return null;
  
  try {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return null;
    
    return parseInt(new Intl.DateTimeFormat(SGT_LOCALE, {
      timeZone: SGT_TIMEZONE,
      year: 'numeric'
    }).format(dateObj));
  } catch (error) {
    return null;
  }
};

/**
 * Format a date range smartly to reduce redundancy on smaller screens
 * - Same year: "15 Dec - 20 Dec 2025" (year only at end)
 * - Different years: "28 Dec 2025 - 3 Jan 2026" (show both years)
 * - Same day: Returns single formatted date
 * @param {string|Date} startDate - Start date of range
 * @param {string|Date} endDate - End date of range
 * @returns {string} Formatted date range string
 */
export const formatDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) {
    // Fallback to single date if one is missing
    return formatDate(startDate || endDate);
  }

  try {
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return 'Invalid Date Range';
    }

    // Check if same day
    if (start.toDateString() === end.toDateString()) {
      return formatDate(start);
    }

    // Get year values in Singapore timezone
    const startYear = new Intl.DateTimeFormat(SGT_LOCALE, {
      timeZone: SGT_TIMEZONE,
      year: 'numeric'
    }).format(start);

    const endYear = new Intl.DateTimeFormat(SGT_LOCALE, {
      timeZone: SGT_TIMEZONE,
      year: 'numeric'
    }).format(end);

    // Format start date (day + month only if same year)
    const startFormatted = start.toLocaleDateString(SGT_LOCALE, {
      day: '2-digit',
      month: 'short',
      ...(startYear !== endYear && { year: 'numeric' }),
      timeZone: SGT_TIMEZONE
    });

    // Format end date (always include year)
    const endFormatted = end.toLocaleDateString(SGT_LOCALE, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: SGT_TIMEZONE
    });

    return `${startFormatted} - ${endFormatted}`;
  } catch (error) {
    console.error('Error formatting date range:', error);
    return 'Invalid Date Range';
  }
};

/**
 * Get data freshness with stale indicator for CV data entry sections
 * Uses date-only comparison in Singapore timezone to avoid timezone edge cases
 * @param {string|Date} updatedAt - Last updated timestamp
 * @returns {{label: string, isStale: boolean}} Object with display label and stale flag
 */
export const getDataFreshness = (updatedAt) => {
  if (!updatedAt) {
    return { label: 'Unknown', isStale: true };
  }

  try {
    // Parse the date - backend sends UTC timestamps
    const updated = new Date(updatedAt);
    if (isNaN(updated.getTime())) {
      return { label: 'Invalid Date', isStale: true };
    }

    // Use date-only comparison in Singapore timezone to avoid timezone edge cases
    // This prevents "0 days ago" from showing incorrectly due to UTC vs SGT differences
    const updatedDateStr = extractDateInSingaporeTimezone(updated);
    const todayDateStr = getTodayInSingaporeTimezone();

    if (!updatedDateStr || !todayDateStr) {
      return { label: 'Unknown', isStale: true };
    }

    // Parse date components and create Date objects for accurate day difference
    const [updatedYear, updatedMonth, updatedDay] = updatedDateStr.split('-').map(Number);
    const [todayYear, todayMonth, todayDay] = todayDateStr.split('-').map(Number);
    
    // Create Date objects using local time (will be interpreted in user's timezone, but we only care about day difference)
    // Since we're comparing dates extracted in Singapore timezone, the day difference will be correct
    const updatedDateOnly = new Date(updatedYear, updatedMonth - 1, updatedDay);
    const todayDateOnly = new Date(todayYear, todayMonth - 1, todayDay);

    // Calculate difference in days using date-only comparison
    const diffMs = todayDateOnly - updatedDateOnly;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    // Handle edge cases
    if (diffDays < 0) {
      // Future date (shouldn't happen, but handle gracefully)
      return { label: 'Invalid Date', isStale: true };
    }

    // For same-day updates, check if it's very recent (within last hour)
    if (diffDays === 0) {
      const now = getCurrentSingaporeTime();
      const updatedTime = toSingaporeTime(updated);
      const diffMs = now - updatedTime;
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMinutes = Math.floor(diffMs / (1000 * 60));

      if (diffMinutes < 1) {
        return { label: 'Just now', isStale: false };
      }
      if (diffMinutes < 60) {
        return { label: `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`, isStale: false };
      }
      if (diffHours < 24) {
        return { label: `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`, isStale: false };
      }
      return { label: 'Today', isStale: false };
    }

    // For recent updates (within 7 days)
    if (diffDays < 7) {
      return { label: `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`, isStale: false };
    }

    // For updates within a month
    if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return { label: `${weeks} week${weeks !== 1 ? 's' : ''} ago`, isStale: false };
    }

    // For updates within 90 days (stale warning)
    if (diffDays < 90) {
      const months = Math.floor(diffDays / 30);
      return { label: `${months} month${months !== 1 ? 's' : ''} ago`, isStale: true };
    }

    // For very old updates
    const years = Math.floor(diffDays / 365);
    return { label: `${years} year${years !== 1 ? 's' : ''} ago`, isStale: true };
  } catch (error) {
    console.error('Error calculating data freshness:', error);
    return { label: 'Unknown', isStale: true };
  }
};

/**
 * Format education year range for display
 * Handles cases where start year may be missing (many certificates don't include start year)
 * 
 * @param {number|null|undefined} startYear - Start year (can be null, undefined, 0, or valid year)
 * @param {number|null|undefined} endYear - End year (can be null, undefined, or valid year)
 * @param {boolean} isOngoing - Whether education is currently ongoing
 * @returns {string} Formatted year range string
 * 
 * @example
 * formatEducationYearRange(2018, 2022, false) // "2018 - 2022"
 * formatEducationYearRange(null, 2022, false) // "2022"
 * formatEducationYearRange(2018, null, true) // "2018 - Present"
 * formatEducationYearRange(null, null, true) // "Present"
 */
export const formatEducationYearRange = (startYear, endYear, isOngoing = false) => {
  // Normalize values: treat 0, null, undefined as missing
  const hasStartYear = startYear && startYear > 0;
  const hasEndYear = endYear && endYear > 0;
  
  if (isOngoing) {
    // Ongoing education
    if (hasStartYear) {
      return `${startYear} - Present`;
    }
    return 'Present';
  }
  
  // Completed education
  if (hasStartYear && hasEndYear) {
    return `${startYear} - ${endYear}`;
  }
  
  if (hasEndYear) {
    // Only end year available (common case - many certificates don't have start year)
    return `${endYear}`;
  }
  
  if (hasStartYear) {
    // Only start year (edge case)
    return `Started ${startYear}`;
  }
  
  // No years available (shouldn't happen in practice, but handle gracefully)
  return 'Year not specified';
};

/**
 * Convert 24-hour time string (HH:MM) to 12-hour format (e.g., "10pm", "7am")
 * @param {string} timeString - Time in HH:MM format (e.g., "22:00", "07:00")
 * @returns {string} Time in 12-hour format (e.g., "10pm", "7am")
 */
export const formatTimeString12Hour = (timeString) => {
  if (!timeString || typeof timeString !== 'string') return '';
  
  const [hours, minutes] = timeString.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) return timeString;
  
  const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  const period = hours < 12 ? 'am' : 'pm';
  
  // If minutes are 00, omit them (e.g., "10pm" not "10:00pm")
  if (minutes === 0) {
    return `${hour12}${period}`;
  }
  
  return `${hour12}:${minutes.toString().padStart(2, '0')}${period}`;
};

/**
 * Format a week-start date as a range: "16–22 Feb" (same month) or "28 Feb–6 Mar" (cross-month).
 * @param {string|Date} weekStart - The Monday date of the week (ISO string or Date).
 * @returns {string} Formatted week range.
 */
export const formatWeekRange = (weekStart) => {
  if (!weekStart) return '';
  const start = new Date(weekStart);
  if (isNaN(start.getTime())) return '';
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  const startDay = start.toLocaleDateString(SGT_LOCALE, { day: 'numeric', timeZone: SGT_TIMEZONE });
  const endLabel = formatDateShort(end);
  if (start.getMonth() === end.getMonth()) {
    return `${startDay}–${endLabel}`;
  }
  return `${formatDateShort(start)}–${endLabel}`;
};

/**
 * Format a total service months count as a human-readable tenure string.
 * Below 12 months: shown in months only ("X month(s)").
 * At exact year boundaries: shown in years only ("X year(s)").
 * Otherwise: abbreviated compound ("X yr(s) Y mo").
 * @param {number|null|undefined} totalMonths - Total completed months of service.
 * @returns {string} Human-readable service period string.
 * @example
/**
 * formatServicePeriod(0)  // "< 1 month"
 * formatServicePeriod(1)  // "1 month"
 * formatServicePeriod(11) // "11 months"
 * formatServicePeriod(12) // "1 year"
 * formatServicePeriod(13) // "1 yr 1 mo"
 * formatServicePeriod(24) // "2 years"
 * formatServicePeriod(27) // "2 yrs 3 mo"
 */
export const formatServicePeriod = (totalMonths) => {
  if (totalMonths == null || isNaN(totalMonths)) return '—';
  const months = Math.max(0, Math.floor(totalMonths));
  if (months === 0) return '< 1 month';
  if (months < 12) return `${months} ${months === 1 ? 'month' : 'months'}`;
  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;
  if (remainingMonths === 0) return `${years} ${years === 1 ? 'year' : 'years'}`;
  return `${years} ${years === 1 ? 'yr' : 'yrs'} ${remainingMonths} mo`;
};

/** Same constants as staff_core.utils.experience_duration (WPOL / Skills Upgrading). */
const EXPERIENCE_DAYS_PER_YEAR = 365.25;
const EXPERIENCE_DAYS_PER_MONTH = 30.44;

/**
 * Format a calendar-day employment span the same way as Skills Upgrading.
 * Sum days across roles first, then call this — never sum truncated months.
 *
 * @param {number|null|undefined} totalDays
 * @returns {string}
 * @example
 * formatExperienceFromDays(1080) // "2 yrs 11 mo"
 * formatExperienceFromDays(746)  // "2 years"
 * formatExperienceFromDays(334)  // "10 months"
 */
export const formatExperienceFromDays = (totalDays) => {
  if (totalDays == null || isNaN(totalDays)) return '—';
  const days = Math.max(0, Math.floor(totalDays));
  if (days <= 0) return '0 days';

  const years = Math.floor(days / EXPERIENCE_DAYS_PER_YEAR);
  const remainingDays = days - Math.floor(years * EXPERIENCE_DAYS_PER_YEAR);
  const months = Math.floor(remainingDays / EXPERIENCE_DAYS_PER_MONTH);

  if (years > 0 && months > 0) {
    return `${years} ${years === 1 ? 'yr' : 'yrs'} ${months} mo`;
  }
  if (years > 0) {
    return `${years} ${years === 1 ? 'year' : 'years'}`;
  }
  if (months > 0) {
    return `${months} ${months === 1 ? 'month' : 'months'}`;
  }
  return `${days} ${days === 1 ? 'day' : 'days'}`;
};

/**
 * LinkedIn-style period for work experience.
 * Month-only entries (1st → last day of month) render as "Feb 2024 – Sep 2024".
 *
 * @param {string|Date|null|undefined} startDate
 * @param {string|Date|null|undefined} endDate
 * @param {boolean} [isCurrent=false]
 * @returns {string}
 */
export const formatWorkExperiencePeriod = (startDate, endDate, isCurrent = false) => {
  const start = parseLocalDate(startDate);
  if (!start) return '—';

  const isMonthStart = start.getDate() === 1;
  const startLabel = isMonthStart
    ? start.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
    : formatDate(start);

  if (isCurrent || !endDate) {
    return `${startLabel} – Present`;
  }

  const end = parseLocalDate(endDate);
  if (!end) return startLabel;

  const lastDay = new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate();
  const isMonthEnd = end.getDate() === lastDay;
  const endLabel = isMonthStart && isMonthEnd
    ? end.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
    : formatDate(end);

  return `${startLabel} – ${endLabel}`;
};

/**
 * Format a decimal hours value as "Xh Ym" (e.g., 8.75 → "8h 45m").
 * For whole hours, omits the minutes (e.g., 8.0 → "8h").
 * For values under 1 hour, shows only minutes (e.g., 0.5 → "30m").
 * When ``withDays`` is true and the span is 24 hours or more, switch to
 * ``2d 22h`` so multi-day waits stay scannable.
 *
 * @param {number|null|undefined} decimalHours - Hours as a decimal.
 * @param {{ withDays?: boolean }} [options]
 * @returns {string} Human-readable hours/minutes string, or '—' for null.
 */
export const formatHoursMinutes = (decimalHours, options = {}) => {
  if (decimalHours == null || isNaN(decimalHours)) return '—';
  const { withDays = false } = options;
  const totalMinutes = Math.round(Math.abs(decimalHours) * 60);
  const sign = decimalHours < 0 ? '-' : '';
  if (totalMinutes === 0) return '0h';

  if (withDays && totalMinutes >= 24 * 60) {
    let days = Math.floor(totalMinutes / (24 * 60));
    let remainingMinutes = totalMinutes % (24 * 60);
    let remainingHours = Math.floor(remainingMinutes / 60);
    if (remainingMinutes % 60 >= 30) remainingHours += 1;
    if (remainingHours >= 24) {
      days += 1;
      remainingHours = 0;
    }
    if (remainingHours === 0) return `${sign}${days}d`;
    return `${sign}${days}d ${remainingHours}h`;
  }

  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h === 0) return `${sign}${m}m`;
  if (m === 0) return `${sign}${h}h`;
  return `${sign}${h}h ${m}m`;
};